/**
 * `{success, message, data}` — one envelope, unwrapped in exactly one place.
 *
 * Components receive `data` and branch on `error_code`, NEVER on `message`:
 * `message` is prose written for a human and is free to change without a
 * contract bump, while `error_code` is the contract.
 */

export interface SuccessEnvelope<T> {
  success: true;
  message: string;
  data: T;
}

export interface FailureDetail {
  /** Field-level code — drives `setError(name, …)` on a form. */
  code: string;
  /** Dotted path of the offending field, where the failure is field-level. */
  field?: string;
}

export interface FailureEnvelope {
  success: false;
  message: string;
  data: unknown;
  error_code: string;
  details?: FailureDetail[];
}

export type Envelope<T> = SuccessEnvelope<T> | FailureEnvelope;

/**
 * Reads a list payload however the answering service shaped it.
 *
 * **The envelope is fixed; the payload is not.** `api-and-data-contracts.md` §2
 * says an empty collection is `data: []` and that pagination metadata is part of
 * the contract — so a paginated route answers `data.items` and a whole-vocabulary
 * route answers `data` as a bare array, and both are correct. This repo reads
 * both kinds.
 *
 * Reading only one of the two shapes is not caught at the boundary — `data` is
 * whatever the service sent — it is a `TypeError` on `payload.items.map` deep
 * inside a component, which reaches the reader as the generic «تعذّر إتمام
 * الطلب» on a surface whose data arrived perfectly intact.
 *
 * Anything that is neither shape reads as empty rather than throwing: a rail
 * with no items is absent, which is what this product already does with a shelf
 * whose source yields nothing.
 */
export function asRows<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (typeof payload === 'object' && payload !== null) {
    const { items } = payload as { items?: unknown };
    if (Array.isArray(items)) return items as T[];
  }
  return [];
}

/** Every rejected read or write in this repo throws exactly this. */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly xRequestId: string | null;
  readonly details: FailureDetail[];
  /** Seconds, from `data.retry_after` — equal to the `Retry-After` header. */
  readonly retryAfter: number | null;

  constructor(init: {
    status: number;
    errorCode: string;
    message: string;
    xRequestId: string | null;
    details?: FailureDetail[];
    retryAfter?: number | null;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.errorCode = init.errorCode;
    this.xRequestId = init.xRequestId;
    this.details = init.details ?? [];
    this.retryAfter = init.retryAfter ?? null;
  }
}

function readRetryAfter(payload: unknown, header: string | null): number | null {
  if (payload && typeof payload === 'object' && 'retry_after' in payload) {
    const value = (payload as { retry_after: unknown }).retry_after;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  const parsed = header === null ? Number.NaN : Number.parseInt(header, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The single request id behind an `X-Request-ID` header.
 *
 * The edge can emit the header more than once — a proxy that sets it and an
 * upstream that echoes it produce two identical values — and `Headers.get`
 * joins repeats with `", "` per the Fetch specification. The raw string then
 * reaches telemetry as «abc, abc», which correlates to no log line at all.
 * Split, trim, drop blanks, and keep the first: repeats of one id collapse,
 * and a genuinely different second value would be the edge's own defect. The
 * id is never rendered — it goes to telemetry and nowhere else.
 */
export function normaliseRequestId(header: string | null): string | null {
  if (header === null) return null;
  const first = header
    .split(',')
    .map((part) => part.trim())
    .find((part) => part.length > 0);
  return first ?? null;
}

/**
 * Unwraps one response. Throws `ApiError` on `success: false`, on a non-2xx
 * status, and on a body that is not the envelope at all — an edge returning
 * its own HTML error page is a failure, not a payload.
 */
export async function parseEnvelope<T>(response: Response): Promise<T> {
  // nginx is the only source of this value. Never generate one here.
  const xRequestId = normaliseRequestId(response.headers.get('X-Request-ID'));

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const envelope = body as Partial<FailureEnvelope> & Partial<SuccessEnvelope<T>>;
  const isEnvelope =
    envelope !== null && typeof envelope === 'object' && typeof envelope.success === 'boolean';

  if (!isEnvelope) {
    throw new ApiError({
      status: response.status,
      errorCode: 'CLIENT_MALFORMED_RESPONSE',
      message: 'استجابة غير متوقّعة من الخادم.',
      xRequestId,
    });
  }

  if (envelope.success === true && response.ok) {
    return envelope.data as T;
  }

  throw new ApiError({
    status: response.status,
    errorCode: envelope.error_code ?? 'CLIENT_UNKNOWN_ERROR',
    message: envelope.message ?? '',
    xRequestId,
    details: envelope.details ?? [],
    retryAfter: readRetryAfter(envelope.data, response.headers.get('Retry-After')),
  });
}
